import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TChatMode = 'astroops' | 'feedback';

export interface IChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

/** Feedback sessions auto-rotate after this many milliseconds of age. */
const FEEDBACK_SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface AstroBotState {
  // ── AstroOps ──────────────────────────────────────────────────────────────
  sessionId: string;
  messages: IChatMessage[];
  // ── Feedback ──────────────────────────────────────────────────────────────
  feedbackMessages: IChatMessage[];
  feedbackSessionId: string;
  feedbackSessionCreatedAt: number;
  // ── Shared ────────────────────────────────────────────────────────────────
  activeMode: TChatMode;
  isOpen: boolean;
  isLoading: boolean;
  // ── Actions ───────────────────────────────────────────────────────────────
  addMessage: (message: IChatMessage) => void;
  addFeedbackMessage: (message: IChatMessage) => void;
  setLoading: (loading: boolean) => void;
  setOpen: (open: boolean) => void;
  setActiveMode: (mode: TChatMode) => void;
  clearSession: () => void;
  clearFeedback: () => void;
  /** Rotates feedbackSessionId if TTL has expired. Returns the current (possibly new) id. */
  getFeedbackSessionId: () => string;
}

const generateSessionId = (): string => `${Date.now()}-${crypto.randomUUID()}`;

const STORE_KEY = 'ap_astro_bot_store_v2';

export const useAstroBotStore = create<AstroBotState>()(
  persist(
    (set, get) => ({
      sessionId: generateSessionId(),
      messages: [],
      feedbackMessages: [],
      feedbackSessionId: generateSessionId(),
      feedbackSessionCreatedAt: Date.now(),
      activeMode: 'astroops',
      isOpen: false,
      isLoading: false,

      addMessage: (message) =>
        set((state) => ({ messages: [...state.messages, message] })),

      addFeedbackMessage: (message) =>
        set((state) => ({ feedbackMessages: [...state.feedbackMessages, message] })),

      setLoading: (loading) => set({ isLoading: loading }),
      setOpen: (open) => set({ isOpen: open }),
      setActiveMode: (mode) => set({ activeMode: mode, isLoading: false }),

      clearSession: () =>
        set({ sessionId: generateSessionId(), messages: [], isLoading: false }),

      clearFeedback: () =>
        set({
          feedbackMessages: [],
          feedbackSessionId: generateSessionId(),
          feedbackSessionCreatedAt: Date.now(),
          isLoading: false,
        }),

      getFeedbackSessionId: () => {
        const { feedbackSessionId, feedbackSessionCreatedAt } = get();
        const expired = Date.now() - feedbackSessionCreatedAt > FEEDBACK_SESSION_TTL_MS;
        if (expired) {
          const newId = generateSessionId();
          set({ feedbackSessionId: newId, feedbackSessionCreatedAt: Date.now(), feedbackMessages: [] });
          return newId;
        }
        return feedbackSessionId;
      },
    }),
    {
      name: STORE_KEY,
      version: 3,
      partialize: (state) => ({
        sessionId: state.sessionId,
        messages: state.messages,
        feedbackMessages: state.feedbackMessages,
        feedbackSessionId: state.feedbackSessionId,
        feedbackSessionCreatedAt: state.feedbackSessionCreatedAt,
        activeMode: state.activeMode,
        isOpen: state.isOpen,
      }),
    }
  )
);

export default useAstroBotStore;
